import { useState } from "react"
import { trpc } from "../../lib/trpc"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import Papa from "papaparse"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select"
import { Label } from "../../components/ui/label"
import { toast } from "sonner"
import {
  Loader2,
  MoreHorizontal,
  Plus,
  Search,
  Pencil,
  Trash,
  Eye,
  EyeOff,
  RefreshCw,
  Ban,
  CheckCircle,
  Upload,
  Download,
  Link2,
} from "lucide-react"

export function AdminResidentsPage() {
  const [search, setSearch] = useState("")
  const [otpVisibility, setOtpVisibility] = useState<Record<string, boolean>>({})
  
  const [isAddOpen, setIsAddOpen] = useState(false)
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [isSuspendOpen, setIsSuspendOpen] = useState(false)
  const [isActivateOpen, setIsActivateOpen] = useState(false)
  const [isManageUnitsOpen, setIsManageUnitsOpen] = useState(false)
  const [selectedUnitId, setSelectedUnitId] = useState("")
  const [isImportOpen, setIsImportOpen] = useState(false)
  const [importMode, setImportMode] = useState<"residents" | "units">("residents")
  const [selectedResident, setSelectedResident] = useState<any>(null)

  const [importRows, setImportRows] = useState<any[]>([])
  const [importSummary, setImportSummary] = useState<{ imported: number; skipped: number; errors?: any[] } | null>(null)

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    buildingId: "",
    apartmentNumber: "",
  })

  const utils = trpc.useUtils()

  const { data: residents, isLoading } = trpc.admin.listResidents.useQuery({
    search: search || undefined,
  })

  const { data: buildings } = trpc.buildings.list.useQuery()
  const { data: csvData } = trpc.admin.exportResidents.useQuery()

  const createMutation = trpc.admin.createResident.useMutation({
    onSuccess: (data) => {
      toast.success(`Resident created successfully. OTP: ${data.otp}`)
      setIsAddOpen(false)
      resetForm()
      utils.admin.listResidents.invalidate()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const updateMutation = trpc.admin.updateResident.useMutation({
    onSuccess: () => {
      toast.success("Resident updated successfully")
      setIsEditOpen(false)
      setSelectedResident(null)
      resetForm()
      utils.admin.listResidents.invalidate()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const deleteMutation = trpc.admin.deleteResident.useMutation({
    onSuccess: () => {
      toast.success("Resident deleted successfully")
      setIsDeleteOpen(false)
      setSelectedResident(null)
      utils.admin.listResidents.invalidate()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const suspendMutation = trpc.admin.suspendResident.useMutation({
    onSuccess: () => {
      toast.success("Resident suspended successfully")
      setIsSuspendOpen(false)
      setSelectedResident(null)
      utils.admin.listResidents.invalidate()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const activateMutation = trpc.admin.activateResident.useMutation({
    onSuccess: () => {
      toast.success("Resident activated successfully")
      setIsActivateOpen(false)
      setSelectedResident(null)
      utils.admin.listResidents.invalidate()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const regenerateOtpMutation = trpc.admin.regenerateOtp.useMutation({
    onSuccess: (data) => {
      toast.success(`New OTP generated: ${data.otp}`)
      utils.admin.listResidents.invalidate()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const importMutation = trpc.admin.importResidents.useMutation({
    onSuccess: (data) => {
      setImportSummary({ imported: data.imported, skipped: data.skipped })
      utils.admin.listResidents.invalidate()
      if (data.errors.length > 0) {
        toast.warning(`Import completed with ${data.errors.length} errors`)
      } else {
        toast.success(`Successfully imported ${data.imported} residents`)
      }
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const importUnitsMutation = trpc.admin.importUnits.useMutation({
    onSuccess: (data: any) => {
      setImportSummary({ imported: data.imported, skipped: data.errors.length, errors: data.errors })
      utils.admin.listResidents.invalidate()
      if (data.errors.length > 0) {
        toast.warning(`Import completed with ${data.errors.length} errors`)
      } else {
        toast.success(`Successfully imported ${data.imported} units (and updated ${data.updated})`)
      }
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const { data: allUnits } = trpc.buildings.listUnits.useQuery()

  const linkUnitMutation = trpc.admin.linkUnit.useMutation({
    onSuccess: () => {
      toast.success("Unit linked successfully")
      setSelectedUnitId("")
      utils.admin.listResidents.invalidate()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const unlinkUnitMutation = trpc.admin.unlinkUnit.useMutation({
    onSuccess: () => {
      toast.success("Unit unlinked successfully")
      utils.admin.listResidents.invalidate()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (event) => {
      const text = event.target?.result as string
      const lines = text.trim().split("\n")
      if (lines.length < 2) {
        toast.error("File is empty or missing header")
        return
      }

      const parsed = Papa.parse(text.trim(), {
        delimiter: text.includes('\t') ? '\t' : ',',
        skipEmptyLines: true,
        header: false,
      })

      const dataRows = (parsed.data as string[][]).slice(1)

      const rows = dataRows.map((values, _index) => {
        if (!values || values.every(v => !v || !v.trim())) return null

        if (importMode === "residents") {
          const [name, apartment, phone, buildingCode, email] = values.map(v => (v || '').trim())
          
          const errors: string[] = []
          if (!name) errors.push("Name is required")
          if (!apartment) {
            errors.push("Apartment is required")
          } else if (!/^[a-zA-Z0-9-]+$/.test(apartment)) {
            errors.push("Invalid apartment format (alphanumeric only)")
          }
          
          if (!phone) {
            errors.push("Phone is required")
          } else if (!/^\+?[0-9]{10,15}$/.test(phone)) {
            errors.push("Invalid phone format")
          }

          if (!buildingCode) errors.push("Building code is required")
          
          const buildingExists = buildings?.some(b => b.code === buildingCode)
          if (buildingCode && !buildingExists) {
            errors.push(`Building code ${buildingCode} not found`)
          }

          return {
            name,
            apartment,
            phone,
            buildingCode,
            email,
            errors,
            isValid: errors.length === 0
          }
        } else {
          const [
            tenantCode, rcCode, buildingCode, blockNo, floorNo, unitNo, billingAccountNo,
            areaNet, areaGross, unitType, saleStatus, repairStatus, heatingType,
            ownerName, ownerPhone
          ] = values.map(v => (v || '').trim())

          const errors: string[] = []
          
          if (!tenantCode) errors.push("Tenant code required")
          if (!rcCode) errors.push("RC code required")
          if (!unitNo) errors.push("Unit no required")
          
          return {
            tenantCode,
            rcCode,
            buildingCode,
            blockNo,
            floorNo,
            unitNo,
            billingAccountNo,
            areaNet,
            areaGross,
            unitType,
            saleStatus,
            repairStatus,
            heatingType,
            ownerName,
            ownerPhone,
            errors,
            isValid: errors.length === 0
          }
        }
      }).filter(Boolean) as any[]

      setImportRows(rows)
    }
    reader.readAsText(file)
  }

  const handleImport = () => {
    const validRows = importRows.filter(r => r.isValid)
    if (validRows.length === 0) {
      toast.error("No valid rows to import")
      return
    }

    if (importMode === "residents") {
      const csvData = [
        "Name,Apartment,Phone,BuildingCode,Email",
        ...validRows.map(r => `${r.name},${r.apartment},${r.phone},${r.buildingCode},${r.email || ""}`)
      ].join("\n")

      importMutation.mutate({ csvData })
    } else {
      const header = [
        "Tenant Code", "RC Code", "Building Code", "Block No", "Floor No", "Unit No", 
        "Billing Account No", "Area Net", "Area Gross", "Unit Type", "Sale Status", 
        "Repair Status", "Heating Type", "Owner Name", "Owner Phone"
      ].join("\t")

      const csvData = [
        header,
        ...validRows.map(r => [
          r.tenantCode, r.rcCode, r.buildingCode, r.blockNo, r.floorNo, r.unitNo,
          r.billingAccountNo, r.areaNet, r.areaGross, r.unitType, r.saleStatus,
          r.repairStatus, r.heatingType, r.ownerName, r.ownerPhone
        ].join("\t"))
      ].join("\n")
      
      importUnitsMutation.mutate({ csvData })
    }
  }

  const handleAdd = () => {
    createMutation.mutate({
      name: formData.name,
      phone: formData.phone,
      email: formData.email || undefined,
      buildingId: formData.buildingId,
      apartmentNumber: formData.apartmentNumber,
    })
  }

  const handleUpdate = () => {
    if (!selectedResident) return
    updateMutation.mutate({
      id: selectedResident.id,
      name: formData.name,
      phone: formData.phone,
      email: formData.email || undefined,
    })
  }

  const handleDelete = () => {
    if (!selectedResident) return
    deleteMutation.mutate({ id: selectedResident.id })
  }

  const handleSuspend = () => {
    if (!selectedResident) return
    suspendMutation.mutate({ id: selectedResident.id })
  }

  const handleActivate = () => {
    if (!selectedResident) return
    activateMutation.mutate({ id: selectedResident.id })
  }

  const handleRegenerateOtp = (id: string) => {
    regenerateOtpMutation.mutate({ id })
  }

  const toggleOtpVisibility = (id: string) => {
    setOtpVisibility((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  const handleExportCSV = () => {
    if (!csvData) {
      toast.error("No data to export")
      return
    }

    const now = new Date()
    const dateStr = now.toISOString().split("T")[0].replace(/-/g, "")
    const filename = `residents-${dateStr}.csv`

    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" })
    const link = document.createElement("a")
    const url = URL.createObjectURL(blob)
    
    link.setAttribute("href", url)
    link.setAttribute("download", filename)
    link.style.visibility = "hidden"
    
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    
    toast.success(`CSV exported as ${filename}`)
  }

  const openEditDialog = (resident: any) => {
    setSelectedResident(resident)
    setFormData({
      name: resident.name,
      phone: resident.phone,
      email: resident.email || "",
      buildingId: resident.unit_owners[0]?.units?.building_id || "",
      apartmentNumber: resident.unit_owners[0]?.units?.unit_no || "",
    })
    setIsEditOpen(true)
  }

  const resetForm = () => {
    setFormData({
      name: "",
      phone: "",
      email: "",
      buildingId: "",
      apartmentNumber: "",
    })
  }

  return (
    <div className="space-y-6">

      <div className="flex flex-col gap-1 md:flex-row md:items-end md:justify-between mb-6">
        <div>
          <p className="text-sm text-muted-foreground">Administration</p>
          <h1 className="text-2xl font-semibold text-foreground mt-1">Residents</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage building residents and their access credentials.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 mt-4 md:mt-0">
          <Button variant="outline" size="sm" onClick={() => setIsImportOpen(true)}>
            <Upload className="mr-2 h-4 w-4" /> Import CSV
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportCSV}>
            <Download className="mr-2 h-4 w-4" /> Export CSV
          </Button>
          <Button size="sm" onClick={() => setIsAddOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> Add Resident
          </Button>
        </div>
      </div>

      <div className="bg-card rounded-xl border border-border shadow-sm">
        <div className="flex items-center p-4 border-b border-border">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search residents..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9 bg-background/50"
            />
          </div>
        </div>

        <div className="relative w-full overflow-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b border-border">
                <TableHead className="w-[180px]">Name</TableHead>
                <TableHead>Username</TableHead>
                <TableHead>Apartment</TableHead>
                <TableHead>Building</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>OTP</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} className="h-24 text-center">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin inline text-primary" />
                    <span className="text-muted-foreground">Loading residents...</span>
                  </TableCell>
                </TableRow>
              ) : residents?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="h-32 text-center text-muted-foreground">
                    No residents found. Add a resident to get started.
                  </TableCell>
                </TableRow>
              ) : (
                residents?.map((resident: any) => {
                  const activeOtp = resident.resident_otps?.find((o: any) => !o.used_at)?.otp || "No OTP"
                  const isOtpVisible = otpVisibility[resident.id]

                  return (
                    <TableRow key={resident.id} className="hover:bg-muted/50 transition-colors border-b border-border last:border-0">
                      <TableCell className="font-medium text-foreground">{resident.name}</TableCell>
                      <TableCell className="text-muted-foreground">{resident.username}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {resident.unit_owners.map((uo: any) => uo.units?.unit_no).join(", ")}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {resident.unit_owners.map((uo: any) => uo.units?.buildings?.name).filter((name: any, index: number, self: any[]) => self.indexOf(name) === index).join(", ")}
                      </TableCell>
                      <TableCell className="text-muted-foreground font-mono text-xs">{resident.phone}</TableCell>
                      <TableCell>
                        <span
                          className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${
                            resident.status === "active"
                              ? "bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-900/30 dark:text-emerald-400 dark:ring-emerald-500/30"
                              : resident.status === "suspended"
                              ? "bg-rose-50 text-rose-700 ring-rose-600/20 dark:bg-rose-900/30 dark:text-rose-400 dark:ring-rose-500/30"
                              : "bg-amber-50 text-amber-700 ring-amber-600/20 dark:bg-amber-900/30 dark:text-amber-400 dark:ring-amber-500/30"
                          }`}
                        >
                          {resident.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <span className="font-mono text-xs bg-muted px-2 py-1 rounded text-muted-foreground min-w-[4rem] text-center">
                            {isOtpVisible ? activeOtp : "••••••"}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-muted-foreground hover:text-foreground"
                            onClick={() => toggleOtpVisibility(resident.id)}
                          >
                            {isOtpVisible ? (
                              <EyeOff className="h-3 w-3" />
                            ) : (
                              <Eye className="h-3 w-3" />
                            )}
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground">
                              <span className="sr-only">Open menu</span>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-[160px]">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuItem onClick={() => openEditDialog(resident)}>
                              <Pencil className="mr-2 h-4 w-4 text-muted-foreground" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleRegenerateOtp(resident.id)}
                            >
                              <RefreshCw className="mr-2 h-4 w-4 text-muted-foreground" /> Regenerate OTP
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedResident(resident)
                                setIsManageUnitsOpen(true)
                              }}
                            >
                              <Link2 className="mr-2 h-4 w-4 text-muted-foreground" /> Manage Units
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {resident.status === "suspended" ? (
                              <DropdownMenuItem
                                onClick={() => {
                                  setSelectedResident(resident)
                                  setIsActivateOpen(true)
                                }}
                                className="text-emerald-600 dark:text-emerald-400"
                              >
                                <CheckCircle className="mr-2 h-4 w-4" /> Activate
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                onClick={() => {
                                  setSelectedResident(resident)
                                  setIsSuspendOpen(true)
                                }}
                                className="text-amber-600 dark:text-amber-400"
                              >
                                <Ban className="mr-2 h-4 w-4" /> Suspend
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedResident(resident)
                                setIsDeleteOpen(true)
                              }}
                              className="text-rose-600 dark:text-rose-400"
                            >
                              <Trash className="mr-2 h-4 w-4" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Resident</DialogTitle>
            <DialogDescription>
              Create a new resident account. They will receive an OTP to login.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                Name
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="phone" className="text-right">
                Phone
              </Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="email" className="text-right">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="buildingId" className="text-right">
                Building ID
              </Label>
              <Input
                id="buildingId"
                value={formData.buildingId}
                onChange={(e) =>
                  setFormData({ ...formData, buildingId: e.target.value })
                }
                className="col-span-3"
                placeholder="UUID"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="apartment" className="text-right">
                Apartment
              </Label>
              <Input
                id="apartment"
                value={formData.apartmentNumber}
                onChange={(e) =>
                  setFormData({ ...formData, apartmentNumber: e.target.value })
                }
                className="col-span-3"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAdd} disabled={createMutation.isPending}>
              {createMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Create Resident
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Resident</DialogTitle>
            <DialogDescription>
              Update resident details.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-name" className="text-right">
                Name
              </Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-phone" className="text-right">
                Phone
              </Label>
              <Input
                id="edit-phone"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-email" className="text-right">
                Email
              </Label>
              <Input
                id="edit-email"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className="col-span-3"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
              {updateMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Are you sure?</DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete the
              resident account and remove their data from our servers.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Delete Resident
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isSuspendOpen} onOpenChange={setIsSuspendOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suspend Resident?</DialogTitle>
            <DialogDescription>
              This will prevent the resident from logging in. You can reactivate
              their account later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsSuspendOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleSuspend}
              disabled={suspendMutation.isPending}
            >
              {suspendMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Suspend Resident
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isActivateOpen} onOpenChange={setIsActivateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Activate Resident?</DialogTitle>
            <DialogDescription>
              This will allow the resident to log in again.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsActivateOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleActivate}
              disabled={activateMutation.isPending}
            >
              {activateMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Activate Resident
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isManageUnitsOpen} onOpenChange={(open) => {
        setIsManageUnitsOpen(open)
        if (!open) setSelectedUnitId("")
      }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Manage Units</DialogTitle>
            <DialogDescription>
              Add or remove units for {selectedResident?.name}.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <h4 className="text-sm font-medium leading-none">Current Units</h4>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Unit</TableHead>
                      <TableHead>Building</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedResident?.unit_owners.map((uo: any) => (
                      <TableRow key={uo.unit_id}>
                        <TableCell>{uo.units?.unit_no}</TableCell>
                        <TableCell>{uo.units?.buildings?.name}</TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive/90"
                            disabled={selectedResident.unit_owners.length <= 1 || unlinkUnitMutation.isPending}
                            onClick={() => unlinkUnitMutation.mutate({
                              userId: selectedResident.id,
                              unitId: uo.unit_id,
                            })}
                            title={selectedResident.unit_owners.length <= 1 ? "Cannot remove last unit" : "Unlink unit"}
                          >
                            {unlinkUnitMutation.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash className="h-4 w-4" />
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="text-sm font-medium leading-none">Add Unit</h4>
              <div className="flex gap-2">
                <Select value={selectedUnitId} onValueChange={setSelectedUnitId}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a unit to add..." />
                  </SelectTrigger>
                  <SelectContent>
                    {buildings?.map((building: any) => {
                      const buildingUnits = allUnits?.filter(
                        (u: any) => 
                          u.building_id === building.id && 
                          !selectedResident?.unit_owners.some((uo: any) => uo.unit_id === u.id)
                      )
                      
                      if (!buildingUnits?.length) return null

                      return (
                        <SelectGroup key={building.id}>
                          <SelectLabel>{building.name}</SelectLabel>
                          {buildingUnits.map((unit: any) => (
                            <SelectItem key={unit.id} value={unit.id}>
                              Unit {unit.unit_no}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      )
                    })}
                  </SelectContent>
                </Select>
                <Button 
                  onClick={() => linkUnitMutation.mutate({
                    userId: selectedResident.id,
                    unitId: selectedUnitId,
                  })}
                  disabled={!selectedUnitId || linkUnitMutation.isPending}
                >
                  {linkUnitMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsManageUnitsOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isImportOpen} onOpenChange={(open) => {
        setIsImportOpen(open)
        if (!open) {
          setImportRows([])
          setImportSummary(null)
        }
      }}>
        <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Import Data</DialogTitle>
            <DialogDescription>
              Upload a file to import data.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4 flex-1 overflow-hidden">
            <div className="flex flex-col gap-4">
              <div className="flex items-center space-x-4">
                <Button 
                    variant={importMode === "residents" ? "default" : "outline"}
                    onClick={() => { setImportMode("residents"); setImportRows([]); setImportSummary(null); }}
                >
                    Residents
                </Button>
                <Button 
                    variant={importMode === "units" ? "default" : "outline"}
                    onClick={() => { setImportMode("units"); setImportRows([]); setImportSummary(null); }}
                >
                    Units
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                {importMode === "residents" 
                    ? "Format: Name, Apartment, Phone, BuildingCode, Email" 
                    : "Format: Tab-delimited file with 15 columns (Tenant Code, RC Code, ...)"}
              </p>
              <Input
                type="file"
                accept={importMode === "residents" ? ".csv" : ".csv,.txt,.tsv"}
                onChange={handleFileChange}
                className="flex-1"
              />
            </div>

            {importSummary && (
              <div className={`border rounded-md p-4 ${importSummary.skipped === 0 ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
                <p className="font-medium">Import Summary:</p>
                <p>{importSummary.imported} imported successfully.</p>
                {importSummary.skipped > 0 && <p className="font-medium">{importSummary.skipped} rows failed.</p>}
                
                {importSummary.errors && importSummary.errors.length > 0 && (
                  <div className="mt-4">
                    <p className="font-medium text-sm mb-2">Error Details (showing first 10):</p>
                    <div className="space-y-2 max-h-48 overflow-y-auto text-xs">
                      {importSummary.errors.slice(0, 10).map((err: any, i: number) => (
                        <div key={i} className="p-2 bg-black bg-opacity-10 rounded">
                          <span className="font-mono">Row {err.row}: {err.reason}</span>
                        </div>
                      ))}
                      {importSummary.errors.length > 10 && (
                        <p className="text-xs italic">... and {importSummary.errors.length - 10} more errors</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {importRows.length > 0 && (
              <div className="border rounded-md overflow-auto flex-1">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {importMode === "residents" ? (
                        <>
                          <TableHead>Name</TableHead>
                          <TableHead>Apartment</TableHead>
                          <TableHead>Phone</TableHead>
                          <TableHead>Building</TableHead>
                          <TableHead>Status</TableHead>
                        </>
                      ) : (
                        <>
                           <TableHead>Unit No</TableHead>
                           <TableHead>Building (RC)</TableHead>
                           <TableHead>Tenant</TableHead>
                           <TableHead>Owner</TableHead>
                           <TableHead>Phone</TableHead>
                           <TableHead>Status</TableHead>
                        </>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {importRows.map((row, i) => (
                      <TableRow key={i} className={!row.isValid ? "bg-red-50" : ""}>
                        {importMode === "residents" ? (
                            <>
                                <TableCell>{row.name}</TableCell>
                                <TableCell>{row.apartment}</TableCell>
                                <TableCell>{row.phone}</TableCell>
                                <TableCell>{row.buildingCode}</TableCell>
                            </>
                        ) : (
                            <>
                                <TableCell>{row.unitNo}</TableCell>
                                <TableCell>{row.rcCode}</TableCell>
                                <TableCell>{row.tenantCode}</TableCell>
                                <TableCell>{row.ownerName}</TableCell>
                                <TableCell>{row.ownerPhone}</TableCell>
                            </>
                        )}
                        <TableCell>
                          {row.isValid ? (
                            <span className="text-green-600 flex items-center">
                              <CheckCircle className="mr-1 h-3 w-3" /> Valid
                            </span>
                          ) : (
                            <span className="text-red-600 text-xs">
                              {row.errors.join(", ")}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsImportOpen(false)}>
              {importSummary ? "Close" : "Cancel"}
            </Button>
            {!importSummary && (
              <Button 
                onClick={handleImport} 
                disabled={(importMode === "residents" ? importMutation.isPending : importUnitsMutation.isPending) || importRows.filter(r => r.isValid).length === 0}
              >
                {(importMode === "residents" ? importMutation.isPending : importUnitsMutation.isPending) && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Import {importRows.filter(r => r.isValid).length} Valid Rows
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
